import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Typography, Button } from '@mui/material';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import MyBots from './pages/MyBots';
import CreateBot from './pages/CreateBot';
import BotDetails from './pages/BotDetails';
import TestChat from './pages/TestChat';
import PublicTestChat from './pages/PublicTestChat'; // Import the new PublicTestChat component
import CustomerServicePage from './pages/CustomerServicePage'; // Import the new CustomerServicePage component
import BetFiBackOffice from './pages/BetFiBackOffice'; // Import the new BetFiBackOffice component
import VerifyEmail from './pages/VerifyEmail'; // Import the new VerifyEmail component

// Updated placeholder components
const EditBot = () => <div>Edit Bot Page</div>;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// NotFound component
const NotFound = () => {
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        textAlign: 'center',
        p: 3,
        background: 'linear-gradient(135deg, #121212 0%, #2c3e50 100%)'
      }}
    >
      <Typography variant="h1" sx={{ mb: 2, fontWeight: 'bold', color: 'white' }}>404</Typography>
      <Typography variant="h5" sx={{ mb: 4, color: 'white' }}>Page Not Found</Typography>
      <Button 
        variant="contained" 
        component={Link}
        to="/"
      >
        Go to Dashboard
      </Button>
    </Box>
  );
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#4C85F0',
    },
    secondary: {
      main: '#F06292',
    },
    background: {
      default: '#f5f7fa',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/bots" element={<ProtectedRoute><MyBots /></ProtectedRoute>} />
            <Route path="/bots/create" element={<ProtectedRoute><CreateBot /></ProtectedRoute>} />
            <Route path="/bots/:id" element={<ProtectedRoute><BotDetails /></ProtectedRoute>} />
            <Route path="/bots/:id/edit" element={<ProtectedRoute><EditBot /></ProtectedRoute>} />
            
            {/* Modified route to support both authenticated and public access */}
            <Route path="/bots/:id/test" element={<TestChat />} />
            
            {/* Public bot access routes */}
            <Route path="/test-chat/:id" element={<TestChat />} />
            <Route path="/public/:id" element={<PublicTestChat />} /> {/* Add new public route */}
            
            <Route path="/support" element={<ProtectedRoute><CustomerServicePage /></ProtectedRoute>} /> {/* Add new support route */}
            <Route path="/betfi-backoffice" element={<ProtectedRoute><BetFiBackOffice /></ProtectedRoute>} /> {/* Add new BetFi BackOffice route */}
            <Route path="/verify-email" element={<VerifyEmail />} /> {/* Add new verify email route */}
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
